import axios from "axios";

const getUsersApi = process.env.REACT_APP_API_URL + "/api/get-users-list";
const getTypesApi = process.env.REACT_APP_API_URL + "/api/get-user-type";
const editUserApi = process.env.REACT_APP_API_URL + "/api/edit-user";
const getUserApi = process.env.REACT_APP_API_URL + "/api/get-user?uid=";

/**
 * Gets the JWT token from cookies
 * @returns {string|null} The JWT token or null if not found
 */
const getAuthToken = () => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("_auth="))
    ?.split("=")[1];

  if (!token) {
    console.warn("Authentication token not found.");
    // Optionally, redirect to login or throw an error
    // throw new Error("Authentication token not found. Please log in again.");
  }

  return token;
};

const fetchUsers = async () => {
  try {
    const token = getAuthToken(); // Get the token
    if (!token) {
      // Handle case where token is not found (e.g., return empty array or throw error)
      return [];
    }

    const response = await axios.get(getUsersApi, {
      headers: {
        Authorization: `Bearer ${token}`, // Add Authorization header
        "Content-Type": "application/json",
      },
      withCredentials: true, // Important for backend CORS to work with credentials
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching Users:", error);
    // Handle specific errors like 401 Unauthorized
    if (error.response && error.response.status === 401) {
      // Handle unauthorized access, e.g., redirect to login
      console.error(
        "Unauthorized access fetching users. Redirecting to login..."
      );
      // window.location.href = '/login'; // Example redirect
    }
    throw error; // Re-throw the error for the calling component to handle if needed
  }
};

const fetchTypes = async () => {
  try {
    const token = getAuthToken(); // Get the token
    if (!token) {
      return []; // Handle missing token
    }
    const response = await axios.get(getTypesApi, {
      headers: {
        Authorization: `Bearer ${token}`, // Add Authorization header
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching Types:", error);
    // Handle specific errors like 401 Unauthorized
    if (error.response && error.response.status === 401) {
      console.error("Unauthorized access fetching user types.");
      // Potentially redirect or show error message
    }
    throw error;
  }
};

const editUser = async (userData) => {
  try {
    const token = getAuthToken(); // Get the token
    if (!token) {
      // Handle missing token case, perhaps by throwing an error or redirecting
      console.error("Authentication token not found for editUser.");
      throw new Error("Authentication token is required to edit user.");
    }

    const response = await axios.post(editUserApi, userData, {
      headers: {
        Authorization: `Bearer ${token}`, // Add Authorization header
        "Content-Type": "application/json", // Ensure content type is set
      },
      // withCredentials might be needed if your backend relies on cookies for other things
      // but for Bearer token auth, it's not strictly necessary for the token itself.
      // However, it's often included for consistency if other calls use it.
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error("Error editing User:", error.response?.data || error.message);
    // It's good practice to throw a more specific error or the error response
    if (error.response && error.response.data && error.response.data.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

const fetchUser = async (uid, token) => {
  try {
    if (!token) {
      // Handle case where token is not found
      throw new Error("Authorization token is required for fetchUser.");
    }
    const response = await axios.get(getUserApi.concat(uid), {
      headers: {
        // Added headers object
        Authorization: `Bearer ${token}`, // Added Authorization header
      },
      // Keep withCredentials if your setup requires it for other reasons
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.error(
      `Error fetching user ${uid}:`,
      error.response?.data || error.message
    );
    // Re-throw the error or handle it as needed
    if (error.response && error.response.status === 401) {
      throw new Error("Unauthorized: Your session may have expired.");
    }
    throw new Error(
      error.response?.data?.error || "Failed to fetch user data."
    );
  }
};

export { fetchUsers, fetchTypes, editUser, fetchUser };
